import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'

// A metadata key that names a time (ts, done_ts, cleaned_at) and a value in this range
// (2000–2100) is shown as a date and time next to the raw number
const TIME_KEY_RE = /^(ts|[a-z0-9_]+_ts|[a-z0-9_]+_at)$/
const TIME_MIN = 946684800
const TIME_MAX = 4102444800

/**
 * This POS window, sent with Max tool calls and echoed in the maxDataChanged socket message of a
 * saved change, so the window that saved it (which already told its tools in-tab) skips it
 */
export const maxWindowId = Math.random().toString(36).slice(2, 12) || 'w'

// Plain-language labels for Johku Max specs. The approval screen must be readable without
// looking at JSON, so every spec fact is rendered with these translated strings.
export default {
  computed: {
    ...mapGetters(['userData', 'timezone', 'dateFormat', 'timeFormat']),
    userLevel () {
      return this.userData ? parseInt(this.userData.userlevel, 10) || 0 : 0
    },
    maxWeekdayNames () {
      return [this._('Mon'), this._('Tue'), this._('Wed'), this._('Thu'), this._('Fri'), this._('Sat'), this._('Sun')]
    },
    maxPeriodLabels () {
      return {
        today: this._('Today'),
        yesterday: this._('Yesterday'),
        last_7_days: this._('Last 7 days'),
        last_30_days: this._('Last 30 days'),
        this_week: this._('This week'),
        last_week: this._('Last week'),
        this_month: this._('This month'),
        last_month: this._('Last month'),
        this_year: this._('This year'),
        last_year: this._('Last year'),
        all_time: this._('All time')
      }
    },
    maxLiveSourceLabels () {
      return {
        monthly_sales: this._('Sales'),
        cumulative_sales: this._('Cumulative sales'),
        top_products: this._('Best-selling products'),
        top_reservable_products: this._('Most reserved products'),
        top_sales_channels: this._('Highest grossing channels'),
        top_customers: this._('Best customers')
      }
    },
    maxCapabilityLabels () {
      return {
        'read:sales': this._('Reads sales'),
        'read:payments': this._('Reads payments'),
        'read:reservations': this._('Reads reservations'),
        'read:customers': this._('Reads customer details'),
        'read:products': this._('Reads products'),
        'read:issues': this._('Reads issues'),
        'read:orders': this._('Reads orders'),
        'read:bills': this._('Reads invoices'),
        'read:resources': this._('Reads resources'),
        'read:categories': this._('Reads categories'),
        'read:tags': this._('Reads tags'),
        'read:discounts': this._('Reads discounts'),
        'read:shows': this._('Reads shows'),
        'read:forms': this._('Reads forms'),
        'read:pricetiers': this._('Reads price tiers'),
        'read:collections': this._('Reads collections'),
        'read:groups': this._('Reads groups'),
        'read:content': this._('Reads content'),
        'read:messages': this._('Reads messages'),
        'read:users': this._('Reads users'),
        'read:poses': this._('Reads points of sale'),
        'read:campaigns': this._('Reads campaigns'),
        'write:products': this._('Changes products'),
        'write:stock': this._('Changes stock counts'),
        'write:issues': this._('Changes issues'),
        'write:orders': this._('Changes orders'),
        'write:resources': this._('Changes resources'),
        'write:categories': this._('Changes categories'),
        'write:tags': this._('Changes tags'),
        'write:discounts': this._('Changes discounts'),
        'write:content': this._('Changes content'),
        'write:messages': this._('Changes messages'),
        'write:customers': this._('Changes customers'),
        'write:reservations': this._('Changes reservations'),
        'deliver:email': this._('Sends email')
      }
    },
    maxOperationLabels () {
      return {
        'products.search': this._('Search products'),
        'products.setVisibility': this._('Show or hide products in the online store'),
        'products.setArchived': this._('Archive or restore products'),
        'products.setEan': this._('Change product barcodes'),
        'stock.setCount': this._('Set stock counts'),
        'issues.list': this._('List issues'),
        'issues.setStatus': this._('Resolve or reopen issues'),
        'orders.setProcessed': this._('Mark orders processed or unprocessed'),
        'orders.setArchived': this._('Archive or restore orders'),
        'orders.delete': this._('Delete unpaid orders'),
        'orders.setWorkflow': this._('Change order workflow'),
        'reservations.setCheckIn': this._('Check reservations in'),
        'reservations.setCheckOut': this._('Check reservations out'),
        'reservations.setResources': this._('Move reservations to other resources'),
        'customers.create': this._('Create customers'),
        'customers.update': this._('Change customer details'),
        'customers.delete': this._('Delete customers'),
        'billingcustomers.update': this._('Change billing customers'),
        'discounts.create': this._('Create discount codes'),
        'discounts.update': this._('Change discount codes'),
        'discounts.delete': this._('Delete discount codes'),
        'content.create': this._('Create content pages'),
        'content.update': this._('Change content pages'),
        'tags.create': this._('Create tags'),
        'tags.update': this._('Change tags'),
        'tags.delete': this._('Delete tags'),
        'resources.create': this._('Create resources'),
        'resources.update': this._('Change resources'),
        'resources.setAvailability': this._('Change resource availability'),
        'reports.query': this._('Read reports'),
        'messages.update': this._('Change messages')
      }
    },
    maxOperationFields () {
      return {
        visible: this._('Visible in the online store'),
        archived: this._('Archived'),
        ean: this._('Barcode'),
        stock: this._('Stock count'),
        status: this._('Status'),
        range: this._('Availability'),
        resources: this._('Resources')
      }
    },
    maxOperationIneligible () {
      return {
        'resources.setAvailability': this._('%s cannot be changed this way: stock resources have no availability, and ranges synced from a calendar are kept.'),
        'products.setEan': this._('%s are not sellable products, so they cannot have a barcode and are skipped.'),
        'products.setVisibility': this._('%s are tickets, which are sold through their event, so they are not shown in the online store and are skipped.')
      }
    },
    maxFilterLabels () {
      return {
        products: this._('Products'),
        categories: this._('Categories'),
        producttypes: this._('Product types'),
        resources: this._('Resources'),
        saleschannels: this._('Sales channels'),
        poses: this._('Points of sale'),
        deliverymethods: this._('Delivery methods'),
        followup_products: this._('Follow-up products'),
        limit: this._('Number of rows'),
        sort: this._('Sort by'),
        min_spend: this._('Minimum spend')
      }
    },
    maxNodeTypes () {
      return {
        heading: { icon: 'fa-heading', label: this._('Heading') },
        text: { icon: 'fa-align-left', label: this._('Text') },
        metric: { icon: 'fa-gauge', label: this._('Key figure') },
        table: { icon: 'fa-table', label: this._('Table') },
        chart: { icon: 'fa-chart-column', label: this._('Chart') },
        finding: { icon: 'fa-flag', label: this._('Finding') },
        findings: { icon: 'fa-list-check', label: this._('Findings') },
        callout: { icon: 'fa-circle-info', label: this._('Note') },
        link: { icon: 'fa-link', label: this._('Web link') },
        section: { icon: 'fa-layer-group', label: this._('Group') },
        grid: { icon: 'fa-table-cells-large', label: this._('Group') },
        stack: { icon: 'fa-layer-group', label: this._('Group') },
        split: { icon: 'fa-table-columns', label: this._('Group') },
        field: { icon: 'fa-i-cursor', label: this._('Input field') },
        button: { icon: 'fa-hand-pointer', label: this._('Button') },
        datatable: { icon: 'fa-table-list', label: this._('Editable table') },
        filterbar: { icon: 'fa-filter', label: this._('Filters') },
        calendar: { icon: 'fa-calendar', label: this._('Calendar') },
        board: { icon: 'fa-columns', label: this._('Board') },
        detail: { icon: 'fa-id-card', label: this._('Details') },
        repeat: { icon: 'fa-clone', label: this._('Repeated list') },
        tabs: { icon: 'fa-folder', label: this._('Tabs') }
      }
    }
  },
  methods: {
    kindLabel (spec) {
      switch (spec && spec.kind) {
        case 'interface':
          return this._('Tool')
        case 'dashboard':
          return this._('Dashboard')
        case 'report':
          return this._('Report')
      }
      return spec && spec.kind ? spec.kind : this._('Report')
    },
    isToolKind (spec) {
      return !!(spec && spec.kind === 'interface')
    },
    isDashboardKind (spec) {
      return !!(spec && spec.kind === 'dashboard')
    },
    isReportKind (spec) {
      return !this.isToolKind(spec) && !this.isDashboardKind(spec)
    },
    needsMyApproval (spec) {
      return !spec.approved && spec.status === 'draft' && this.userLevel >= spec.approval_level
    },
    statusLabel (spec) {
      switch (spec.status) {
        case 'active':
          return this._('Active')
        case 'paused':
          return this._('Paused')
        case 'archived':
          return this._('Archived')
      }
      return this._('Needs approval')
    },
    statusClass (spec) {
      switch (spec.status) {
        case 'active':
          return 'label-success'
        case 'draft':
          return 'label-warning'
      }
      return 'label-default'
    },
    scheduleSummary (schedule) {
      const parts = []
      const weekdays = schedule.weekdays || []
      if (weekdays.length && weekdays.length < 7) {
        parts.push(weekdays.map(d => this.maxWeekdayNames[d - 1]).join(', '))
      } else if (!(schedule.days || []).length) {
        parts.push(this._('Every day'))
      }
      if ((schedule.days || []).length) {
        parts.push(this._('Days of the month: %s').replace('%s', schedule.days.join(', ')))
      }
      if ((schedule.months || []).length) {
        parts.push(this._('Only in months: %s').replace('%s', schedule.months.join(', ')))
      }
      parts.push((schedule.times || []).join(', '))
      return parts.join(' · ')
    },
    sourceName (source) {
      if (source.live) {
        return this.maxLiveSourceLabels[source.live] || source.live
      }
      // stock is a Johku Max source only, not a POS report type
      if (source.report === 'stock') {
        return this._('Stock')
      }
      return this.getReportName ? this.getReportName(source.report) : source.report
    },
    periodLabel (period) {
      return this.maxPeriodLabels[period] || period
    },
    capabilityLabel (capability) {
      return this.maxCapabilityLabels[capability] || capability
    },
    filterText (filter) {
      const label = this.maxFilterLabels[filter.key] || filter.key
      if (Array.isArray(filter.ids)) {
        return label + ': ' + this._('%s selected').replace('%s', filter.ids.length)
      }
      return label + ': ' + filter.value
    },
    nodeType (type) {
      return this.maxNodeTypes[type] || { icon: 'fa-square', label: type }
    },
    operationLabel (operation) {
      return this.maxOperationLabels[operation] || operation
    },
    // key: the metadata key of the change, if any; time keys get a readable date
    operationValue (field, value, key) {
      if (field === 'status') {
        return parseInt(value, 10) === 1 ? this._('Resolved') : this._('Unresolved')
      }
      if (typeof value === 'boolean') {
        return value ? this._('Yes') : this._('No')
      }
      if (value === '' || value === null || value === undefined) {
        return this._('(empty)')
      }
      if (typeof value === 'object') {
        return JSON.stringify(value)
      }
      const time = this.operationTime(key, value)
      return time ? time + ' (' + value + ')' : String(value)
    },
    operationTime (key, value) {
      if (typeof key !== 'string' || !TIME_KEY_RE.test(key)) {
        return null
      }
      const seconds = typeof value === 'number' ? value : (typeof value === 'string' && /^[0-9]{9,10}$/.test(value) ? Number(value) : NaN)
      if (!Number.isInteger(seconds) || seconds < TIME_MIN || seconds > TIME_MAX) {
        return null
      }
      const at = DateTime.fromSeconds(seconds, { zone: this.timezone || 'Europe/Helsinki' })
      return at.isValid ? at.toFormat((this.dateFormat || 'd.M.yyyy') + ' ' + (this.timeFormat || 'H:mm')) : null
    },
    // The metadata key a change row writes (field is metadata.<ns>.<key>)
    metadataKey (change) {
      if (change.key) {
        return change.key
      }
      return typeof change.field === 'string' && change.field.startsWith('metadata.') ? change.field.split('.').pop() : null
    },
    // A change row's own field: the metadata key, or a field that differs from the call's
    operationChangeKey (confirm, change) {
      const key = this.metadataKey(change)
      if (key) {
        return key
      }
      if (change.field && change.field !== confirm.field) {
        return this.maxOperationFields[change.field] || change.field
      }
      return ''
    }
  }
}
